import { generateFlightCheckoutItem } from 'lib/checkout/flights/cart'
import { generatePassengersFromOccupants } from 'checkout/lib/utils/flights/passenger'
import { FlightsFareTypes } from 'constants/flight'
import type { FlightSearchParams } from 'hooks/useSearchFlights'
import type { ModalContextType } from 'contexts/ModalContext'
import {
  mapCheckoutFlightItemToTripChatFlightCartPayload,
  type TripChatFlightCartPayloadItem,
} from 'tripPlanner/api/flightCart'
import { selectFlightFareFamilies } from 'tripPlanner/components/Chat/Flights/selectFlightFareFamilies'

export interface PreparedTripChatFlightCartPayload {
  checkoutItem: App.Checkout.FlightItem
  cartPayload: TripChatFlightCartPayloadItem
  outboundJourney: App.JourneyV2
  returningJourney?: App.JourneyV2
  searchId: string
  sessionId?: string
  searchExpiry: number
  occupants: App.Occupants
  fareType: 'oneWay' | 'return'
  fareFamilies: Array<App.FlightFareFamily | undefined>
}

export type PrepareFlightCheckoutItemResult =
  | { status: 'success'; prepared: PreparedTripChatFlightCartPayload }
  | { status: 'missing_search_id' | 'cancelled' | 'missing_search_params' | 'fare_selection_required' }

export interface PrepareSelectedFlightCheckoutItemArgs {
  departingJourney: App.JourneyV2
  returningJourney?: App.JourneyV2
  searchParams: FlightSearchParams
  searchId: string | undefined
  sessionId: string | undefined
  searchExpiry: number
  showModal: ModalContextType
  /**
   * When false, an unanswered fare question ends in `fare_selection_required`
   * instead of a modal. Background conversions pass false so they only complete
   * silently for a fare the traveller already picked.
   */
  promptForFare?: boolean
}

export async function prepareSelectedFlightCheckoutItem({
  departingJourney,
  returningJourney,
  searchParams,
  searchId,
  sessionId,
  searchExpiry,
  showModal,
  promptForFare = true,
}: PrepareSelectedFlightCheckoutItemArgs): Promise<PrepareFlightCheckoutItemResult> {
  if (!searchId) return { status: 'missing_search_id' }

  const firstItineraryLeg = searchParams.itinerary[0]
  if (!firstItineraryLeg) return { status: 'missing_search_params' }

  const occupants = searchParams.occupancies[0] ?? { adults: 1 }
  const passengers = generatePassengersFromOccupants(occupants)
  const fareType = returningJourney ? 'return' : 'oneWay'

  const fareResult = await selectFlightFareFamilies({
    departingJourney,
    returningJourney,
    occupants,
    searchId,
    sessionId,
    showModal,
    isMultiCity: searchParams.fareType === FlightsFareTypes.MULTI_CITY,
    prompt: promptForFare,
  })
  if (fareResult.status === 'cancelled') return { status: 'cancelled' }
  if (fareResult.status === 'prompt_required') return { status: 'fare_selection_required' }

  const { departingFareFamily, returningFareFamily } = fareResult.selection

  const flights = returningJourney ? [departingJourney, returningJourney] : [departingJourney]
  const fareFamilies = returningJourney
    ? [departingFareFamily, returningFareFamily]
    : [departingFareFamily]

  // Derive the route from the selected journey, not searchParams.itinerary[0]:
  // a multi-city leg's saved searchParams hold the FULL itinerary, so itinerary[0]
  // is the first leg (e.g. SYD→MEL) even when this leg is MEL→PER. Using it sent
  // the wrong departure airport and the backend rejected it with ROUTE_MISMATCH.
  const departingFlights = departingJourney.flightGroup.flights
  const originAirportCode = departingFlights[0]?.departureAirport ?? firstItineraryLeg.departureAirport
  const destinationAirportCode =
    departingFlights[departingFlights.length - 1]?.arrivalAirport ?? firstItineraryLeg.arrivalAirport

  const checkoutItem = generateFlightCheckoutItem({
    originAirportCode,
    destinationAirportCode,
    occupants,
    passengers,
    fareType,
    viewType: fareType === 'oneWay' ? 'OneWay' : 'TwoOneWays',
    searchId,
    flights,
    baggage: [],
    fareFamilies,
    bundledItemIds: [],
    expiry: searchExpiry,
  })

  return {
    status: 'success',
    prepared: {
      checkoutItem,
      cartPayload: mapCheckoutFlightItemToTripChatFlightCartPayload(checkoutItem),
      outboundJourney: departingJourney,
      returningJourney,
      searchId,
      sessionId,
      searchExpiry,
      occupants,
      fareType,
      fareFamilies,
    },
  }
}
